import { Component, ElementRef, EventEmitter, forwardRef, OnDestroy, Output, ViewChild } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import { PhoneInfo } from '@core/model/phone-info';

import { EMPTY, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { getSweetAlertOptions } from '@commons/utility/util';

import { show_popup, TITLE_ERROR } from '@shared/utility/popup-error';

import { retryWithDelay } from '@core/operators/retry-delay';

import { PhoneVerificationService } from './phone-verification.service';

import { INPUT_PATTERNS_CONSTANT } from './rapi-phone.constant';

@Component({
  selector: 'app-rapi-phone',
  templateUrl: './rapi-phone.component.html',
  styleUrls: ['./rapi-phone.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RapiPhoneComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RapiPhoneComponent),
      multi: true,
    },
  ],
})
export class RapiPhoneComponent implements ControlValueAccessor, OnDestroy {
  form: FormGroup;
  subscriptions: Subscription[] = [];
  @ViewChild('phoneInput', { static: false }) phoneInput: ElementRef;

  @Output() public focusEventPhone: EventEmitter<void>;

  constructor(private formBuilder: FormBuilder, private phoneVerificationService: PhoneVerificationService) {
    this.focusEventPhone = new EventEmitter<void>();
    this.form = this.formBuilder.group(
      {
        phone: [
          '',
          [Validators.required, Validators.pattern('^3[0-9]*$'), Validators.minLength(10), Validators.maxLength(10)],
        ],
      },
      {
        updateOn: 'blur',
      }
    );
    this.subscriptions.push(
      this.form.valueChanges.subscribe((value) => {
        this.onChange(value);
        this.onTouched();
      })
    );

    this.form.controls['phone'].setAsyncValidators([this.asyncValidatePhonePipe()]);
    this.form.controls['phone'].statusChanges.subscribe(() => {
      this.form.updateValueAndValidity();
      this.onChange(this.form.value);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  get value(): PhoneInfo {
    return this.form.value;
  }

  set value(value: PhoneInfo) {
    this.form.setValue(value);
    this.onChange(value);
    this.onTouched();
  }

  get phoneControl() {
    return this.form.controls.phone;
  }

  public onFocusEmitInput(): void {
    this.focusEventPhone.emit();
  }

  writeValue(value: any): void {
    if (value) {
      this.value = value;
    }

    if (value === null) {
      this.form.reset();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
      this.phoneInput.nativeElement.focus();
    }
  }

  validate(_: FormControl) {
    if (!this.phoneControl.value || this.phoneControl.invalid || this.phoneControl.pending) {
      return { phone: { valid: false } };
    }
    return null;
  }

  showAlertMessage(message: string, confirmButtonText: string) {
    let options = getSweetAlertOptions(message, confirmButtonText);

    (document.activeElement as HTMLElement)?.blur();
    Swal.fire(options).then(() => {
      this.phoneControl.setValue('');
      setTimeout(() => this.phoneInput.nativeElement.focus());
    });
  }

  private isValidPattern(phoneNumber: string | null): boolean {
    return phoneNumber ? INPUT_PATTERNS_CONSTANT.NUMBER_RANGE.test(phoneNumber) : false;
  }

  asyncValidatePhonePipe(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value || !this.isValidPattern(control.value)) {
        return of(null);
      }

      return of(control.value).pipe(
        debounceTime(3000),
        switchMap(() => {
          if (!this.phoneControl.value) {
            return of({ phoneNumber: true });
          }
          return this.phoneVerificationService.existsByPhone(this.phoneControl.value).pipe(
            map((result) => {
              if (result.response === true) {
                this.showAlertMessage(result.message, 'Rectificar celular');
                return { existing: true };
              }
              return null;
            })
          );
        }),
        retryWithDelay(1000, 3),
        catchError(() => {
          show_popup(
            TITLE_ERROR,
            'Lo sentimos, ocurrió un error al validar tu número de celular. Inténtalo de nuevo  P-131'
          );
          this.phoneControl.setValue(null);
          return EMPTY;
        })
      );
    };
  }

  public checkIsNumber(event: KeyboardEvent): void {
    const char = event.key;

    if (!INPUT_PATTERNS_CONSTANT.NUMBER.test(char)) {
      event.preventDefault();
    }
  }
}
